// Headless behaviour-to-category mapping shared by web and mobile.
//
// This module owns the pure logic that turns a HealthBehaviour into an
// attention-card category (and back), plus the "should we show the when line"
// heuristic. Platform-specific visuals (Ionicons names, hex colours, CSS
// classes) stay in each client; only the data-to-props logic lives here so the
// eyebrow, behaviour, and timing label cannot drift between surfaces.
//
// See docs/src/content/docs/agent-output-sanitisers.md for the broader
// "pure logic in shared, visuals in client" pattern.
/**
 * Maps a core health behaviour to the attention-card category that badges it.
 * Keyed exhaustively by HealthBehaviour: the `satisfies` clause fails the
 * build if a core behaviour is unhandled, so every tagged step renders its
 * chip. The `food` behaviour badges as `nutrition` because the server's
 * category enum uses `nutrition` (the dietary pillar) while the behaviour
 * taxonomy uses `food` (the action verb).
 */
export const BEHAVIOUR_TO_CATEGORY = {
    sleep: "sleep",
    food: "nutrition",
    movement: "movement",
    stress: "stress",
    connection: "connection",
};
/**
 * Reverse of BEHAVIOUR_TO_CATEGORY: given an action category, return the
 * health behaviour that badges it. Keyed exhaustively by ActionCategory.
 */
export const CATEGORY_TO_BEHAVIOUR = {
    sleep: "sleep",
    nutrition: "food",
    movement: "movement",
    stress: "stress",
    connection: "connection",
};
/**
 * Canonical, user-facing label for every core health behaviour. Re-exported
 * from @healthos/shared (the single source of truth) so callers don't need a
 * second import; the object identity is the same.
 */
export { BEHAVIOUR_LABELS } from "@healthos/shared";
/**
 * Resolve the action category from a list of health behaviours on a plan item.
 * Returns the first behaviour's category, or undefined when none are tagged.
 * Used by callers that have a PlanItem (with `healthBehaviours`) but need the
 * attention-card `category` to badge.
 */
export function categoryFromHealthBehaviours(behaviours) {
    for (const behaviour of behaviours ?? []) {
        const category = BEHAVIOUR_TO_CATEGORY[behaviour];
        if (category)
            return category;
    }
    return undefined;
}
/**
 * Resolve the health behaviour that badges an attention card from its
 * category. Returns null when the category is absent (the card omits the
 * chip honestly rather than guess).
 */
export function behaviourFromCategory(category) {
    if (!category)
        return null;
    return CATEGORY_TO_BEHAVIOUR[category];
}
/**
 * The behaviour a row should badge. Returns null when none are tagged or none
 * are recognised, so the row can omit the chip honestly rather than guess.
 * This is the headless counterpart to each client's `primaryBehaviourMeta`
 * (which looks up the visual meta after this returns the behaviour).
 *
 * `preferred` is the set of behaviours the surface is filtered by. An item
 * carries every behaviour it is tagged with, so a Reset tagged both `stress`
 * and `food` matches a Food filter but badged `stress` under the plain
 * first-entry rule, which reads as though the filter is broken. When a filter
 * is active the badge names the behaviour the member filtered by; with no
 * filter, or when the item somehow matches none of them, it falls back to the
 * first tagged behaviour.
 */
export function primaryBehaviour(item, preferred) {
    const behaviours = item.healthBehaviours ?? [];
    if (preferred?.length) {
        for (const behaviour of behaviours) {
            if (behaviour && preferred.includes(behaviour))
                return behaviour;
        }
    }
    for (const behaviour of behaviours) {
        if (behaviour)
            return behaviour;
    }
    return null;
}
/**
 * Hide the "when" line when it mostly repeats the headline instruction. A
 * trimmed empty when, or one that normalises to the title, is suppressed. When
 * both the title and when are at least four words, a >=60% word overlap also
 * suppresses the line (the when adds no information the title doesn't carry).
 */
export function shouldShowWhenLine(title, when) {
    const trimmed = when.trim();
    if (!trimmed)
        return false;
    const normalize = (value) => value
        .trim()
        .toLowerCase()
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ");
    const normalizedTitle = normalize(title);
    const normalizedWhen = normalize(trimmed);
    if (!normalizedWhen || normalizedWhen === normalizedTitle)
        return false;
    const titleWords = normalizedTitle.split(" ").filter(Boolean);
    const whenWords = normalizedWhen.split(" ").filter(Boolean);
    if (titleWords.length >= 4 && whenWords.length >= 4) {
        const overlap = whenWords.filter((word) => titleWords.includes(word)).length;
        const overlapRatio = overlap / Math.min(titleWords.length, whenWords.length);
        if (overlapRatio >= 0.6)
            return false;
    }
    return true;
}
//# sourceMappingURL=behaviour-meta.js.map